import { LocationCompressed } from '@jooxter/api';
import { offCanvasContainerEnum } from '@jooxter/ui';
import {
  AddinBookingOffCanvasTabEnum,
  BookingOffCanvasTabEnum,
  IQuickTimeSlot,
  IRange,
  OffCanvasModeEnum,
} from '@jooxter/utils';
import { IBookingFormPreset } from '../forms/Booking/types/booking-form.types';
import { IFloorExceptionalClosingHoursFormPreset } from '../forms/FloorExceptionalClosingHours/types/floor-exceptional-closing-hours-form.types';
import { IExceptionalOpeningHoursFormPreset } from '../forms/ExceptionalOpeningHours/types/exceptional-opening-hours-form.types';

// One panel active at a time - see offcanvas.store.ts for why this lives as a single store.
export enum OffCanvasKindEnum {
  Booking = 'booking',
  Floor = 'floor',
  Resource = 'resource',
  WorkplaceCalendar = 'workplaceCalendar',
}

// Where the user was before clicking a booking on the planning tab, so the back arrow can return there.
export interface BookingOffCanvasPreviousView {
  bookingId?: number;
  mode?: OffCanvasModeEnum;
  selectedIndex: BookingOffCanvasTabEnum;
  planningDate?: string;
}

export interface BookingOffCanvasState {
  mode?: OffCanvasModeEnum;
  bookingId?: number;
  resourceId?: number;
  preset?: IBookingFormPreset;
  directEdit: boolean;
  autoSelectFirstSlot: boolean;
  slotToBookInstantly: IQuickTimeSlot | null;
  selectedIndex: BookingOffCanvasTabEnum;
  addinSelectedIndex: AddinBookingOffCanvasTabEnum;
  previousView?: BookingOffCanvasPreviousView;
  // ISO date the planning tab must reopen on after coming back from a booking.
  planningDate?: string;
}

export interface FloorOffCanvasState {
  mode?: OffCanvasModeEnum;
  floorId?: number;
  floorExceptionalClosingHoursId?: number;
  preset?: IFloorExceptionalClosingHoursFormPreset;
  selectedBuilding?: LocationCompressed;
}

export interface ResourceOffCanvasState {
  mode?: OffCanvasModeEnum;
  resourceId?: number;
  exceptionalOpeningHoursId?: number;
  preset?: IExceptionalOpeningHoursFormPreset;
}

export interface WorkplaceCalendarOffCanvasState {
  initialRange: IRange;
  onToastActionClicked: (bookingId: number) => void;
}

export interface OffCanvasStoreState {
  isOpen: boolean;
  kind: OffCanvasKindEnum | null;
  container: offCanvasContainerEnum;
  setContainer: (container: offCanvasContainerEnum) => void;
  close: () => void;

  booking: BookingOffCanvasState;
  openBookingView: (bookingId: number) => void;
  openBookingForEdit: (bookingId: number) => void;
  bookingGoToEditView: () => void;
  goToResourceCalendarView: (resourceId?: number, preset?: IBookingFormPreset) => void;
  goToCreateBooking: (resourceId: number, preset: IBookingFormPreset, autoSelectFirstSlot: boolean) => void;
  goToResourceInformations: (resourceId?: number, preset?: IBookingFormPreset) => void;
  goToResourcePlan: () => void;
  onBookingEventClick: (bookingId: number, planningDate?: string) => void;
  bookingGoBack: () => void;
  onBookingCalendarSelect: (preset: IBookingFormPreset, resourceId?: number) => void;
  onBookingSlotSelect: (slot: IQuickTimeSlot, preset: IBookingFormPreset, resourceId?: number) => void;
  setBookingMode: (mode: OffCanvasModeEnum) => void;
  setBookingSelectedIndex: (selectedIndex: BookingOffCanvasTabEnum) => void;
  setAddinBookingSelectedIndex: (addinSelectedIndex: AddinBookingOffCanvasTabEnum) => void;

  floor: FloorOffCanvasState;
  openFloorView: (id: number, floorId: number, selectedBuilding?: LocationCompressed) => void;
  openFloorCreate: (
    floorId: number,
    preset: IFloorExceptionalClosingHoursFormPreset,
    selectedBuilding?: LocationCompressed
  ) => void;
  floorGoToEditView: () => void;
  setFloorMode: (mode: OffCanvasModeEnum) => void;

  resource: ResourceOffCanvasState;
  openResourceView: (id: number, resourceId: number) => void;
  openResourceCreate: (resourceId: number, preset: IExceptionalOpeningHoursFormPreset) => void;
  resourceGoToEditView: () => void;
  setResourceMode: (mode: OffCanvasModeEnum) => void;

  workplaceCalendar: WorkplaceCalendarOffCanvasState | null;
  openWorkplaceCalendar: (payload: WorkplaceCalendarOffCanvasState) => void;
}
